// ============================================================================
// ACCESS MANAGEMENT TAB
// Manage project access control (ACL) - add/remove users and set access levels
// ============================================================================

import React, { useState, useRef, useEffect, useMemo } from 'react';
import { UserPlus, Trash2, Shield, Eye, Edit3, AlertCircle, Search, Lock, X, Users, CheckCircle2, Crown } from 'lucide-react';
import {
  useProjectACL,
  useAddACLEntry,
  useRemoveACLEntry,
  useUpdateACLEntry,
  useTransferOwnership,
  useSearchUsers,
  ProjectACLEntry,
  UserSearchResult,
  BreakGlassContext
} from '../../hooks/useProjects';
import { ConfirmModal } from '../common/ConfirmModal';

interface AccessManagementTabProps {
  projectId: string;
  projectOwnerId: string;
  canManageAcl: boolean;
  /** A non-owner System Administrator's active project grant; sent with every change */
  breakGlass?: BreakGlassContext | null;
}

interface SearchDropdownProps {
  show: boolean;
  query: string;
  isSearching: boolean;
  searchResults: UserSearchResult[];
  selectedUserId?: string;
  ref: React.RefObject<HTMLDivElement | null>;
  onSelect: (user: UserSearchResult) => void;
  getInitials: (email: string) => string;
}

const SearchDropdown = React.forwardRef<HTMLDivElement, Omit<SearchDropdownProps, 'ref'>>(
  ({ show, query, isSearching, searchResults, selectedUserId, onSelect, getInitials }, ref) => {
    if (!show || query.length < 2) return null;

    let dropdownContent: React.ReactNode;
    if (isSearching) {
      dropdownContent = <div className="p-4 text-center text-slate-400 text-sm">Searching...</div>;
    } else if (searchResults.length === 0) {
      dropdownContent = <div className="p-4 text-center text-slate-400 text-sm">No users found</div>;
    } else {
      dropdownContent = (
          <div className="space-y-0.5 p-1">
            <p className="px-3 py-1.5 text-xs font-semibold text-slate-400 uppercase tracking-wider">Search Results</p>
            {searchResults.map((u) => {
              const isSelected = selectedUserId === u.userId;
              return (
                <div
                  key={u.userId}
                  onClick={() => onSelect(u)}
                  className={`flex items-center justify-between p-3 rounded-lg cursor-pointer transition-colors ${isSelected ? 'border border-primary bg-primary/5' : 'hover:bg-slate-50'}`}
                >
                  <div className="flex items-center gap-3">
                    <div className="size-10 rounded-full bg-slate-100 text-slate-500 flex items-center justify-center font-bold text-sm">
                      {getInitials(u.email)}
                    </div>
                    <div className="flex flex-col">
                      <p className="text-navy text-sm font-semibold leading-tight">
                        {u.fullName || `${u.firstName || ''} ${u.lastName || ''}`.trim() || 'Unknown User'}
                      </p>
                      <p className="text-slate-400 text-xs leading-tight">{u.email}</p>
                    </div>
                  </div>
                  {isSelected && <CheckCircle2 size={20} className="text-primary" />}
                </div>
              );
            })}
          </div>
      );
    }

    return (
      <div ref={ref} className="mt-2 bg-white border border-slate-200 rounded-lg shadow-lg max-h-60 overflow-y-auto z-10 relative">
        {dropdownContent}
      </div>
    );
  }
);
SearchDropdown.displayName = 'SearchDropdown';

interface MembersTableProps {
  ownerEmail: string;
  ownerId: string;
  isOwner: boolean;
  filteredAcl: ProjectACLEntry[];
  aclLength: number;
  totalMembers: number;
  filterQuery: string;
  onDeleteUser: (entry: ProjectACLEntry) => void;
  onChangeLevel: (entry: ProjectACLEntry, level: 'read' | 'write') => void;
  onMakeOwner: (entry: ProjectACLEntry) => void;
  busy: boolean;
  getInitials: (email: string) => string;
}

const MembersTable: React.FC<MembersTableProps> = ({
  ownerEmail,
  ownerId,
  isOwner,
  filteredAcl,
  aclLength,
  totalMembers,
  filterQuery,
  onDeleteUser,
  onChangeLevel,
  onMakeOwner,
  busy,
  getInitials
}) => {
  const memberPluralSuffix = totalMembers !== 1 ? 's' : '';

  return (
    <div className="rounded-xl border border-slate-200 bg-white shadow-sm overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full text-left border-collapse">
          <thead>
            <tr className="bg-slate-50 border-b border-slate-200">
              <th className="px-6 py-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">User</th>
              <th className="px-6 py-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">Access Level</th>
              <th className="px-6 py-4 text-xs font-semibold text-slate-500 uppercase tracking-wider hidden md:table-cell">Granted By</th>
              <th className="px-6 py-4 text-xs font-semibold text-slate-500 uppercase tracking-wider hidden sm:table-cell">Granted Date</th>
              {isOwner && (
                <th className="px-6 py-4 text-xs font-semibold text-slate-500 uppercase tracking-wider text-right">Actions</th>
              )}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {/* Owner Row */}
            <tr className="group hover:bg-slate-50 transition-colors">
              <td className="px-6 py-4 whitespace-nowrap">
                <div className="flex items-center gap-3">
                  <div className="size-10 rounded-full bg-primary/20 text-primary flex items-center justify-center font-bold text-sm">
                    {getInitials(ownerEmail)}
                  </div>
                  <div className="flex flex-col">
                    <span className="text-navy font-semibold text-sm">{ownerEmail}</span>
                    <span className="text-slate-400 text-xs font-mono">{ownerId}</span>
                  </div>
                </div>
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-bold bg-primary/10 text-primary border border-primary/20">
                  <Shield size={14} />
                  Owner
                </span>
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-500 hidden md:table-cell">
                System
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-500 hidden sm:table-cell">
                —
              </td>
              {isOwner && (
                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                  <Lock size={18} className="text-slate-300 inline-block" />
                </td>
              )}
            </tr>

            {/* ACL Entries */}
            {filteredAcl.length > 0 ? (
              filteredAcl.map((entry) => (
                <tr key={entry.userId} className="group hover:bg-slate-50 transition-colors">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center gap-3">
                      <div className="size-10 rounded-full bg-indigo-100 text-indigo-600 flex items-center justify-center font-bold text-sm">
                        {getInitials(entry.email)}
                      </div>
                      <div className="flex flex-col">
                        <span className="text-navy font-semibold text-sm">{entry.email}</span>
                        <span className="text-slate-400 text-xs font-mono">{entry.userId}</span>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {isOwner ? (
                      <select
                        value={entry.accessLevel}
                        disabled={busy}
                        onChange={(e) => onChangeLevel(entry, e.target.value as 'read' | 'write')}
                        className="border border-[#e2e8f0] rounded-lg px-2 py-1 text-xs font-medium text-navy bg-white focus:ring-2 focus:ring-primary focus:border-transparent outline-none"
                        aria-label={`Access level for ${entry.email}`}
                      >
                        <option value="read">Read</option>
                        <option value="write">Write</option>
                      </select>
                    ) : entry.accessLevel === 'write' ? (
                      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-medium text-blue-700 bg-blue-50 border border-blue-200">
                        <Edit3 size={12} />
                        Write
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-medium text-slate-600 bg-slate-100 border border-slate-200">
                        <Eye size={12} />
                        Read
                      </span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-500 hidden md:table-cell">
                    {entry.grantedBy || '—'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-500 hidden sm:table-cell">
                    {new Date(entry.grantedAt).toLocaleDateString()}
                  </td>
                  {isOwner && (
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <button
                        onClick={() => onMakeOwner(entry)}
                        disabled={busy}
                        className="text-slate-400 hover:text-primary transition-colors p-1 rounded-full hover:bg-primary/10 mr-1"
                        title="Make owner"
                      >
                        <Crown size={18} />
                      </button>
                      <button
                        onClick={() => onDeleteUser(entry)}
                        className="text-slate-400 hover:text-red-500 transition-colors p-1 rounded-full hover:bg-red-50"
                        title="Remove access"
                      >
                        <Trash2 size={18} />
                      </button>
                    </td>
                  )}
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={isOwner ? 5 : 4} className="px-6 py-12 text-center">
                  <Shield size={48} className="mx-auto mb-4 text-slate-200" />
                  <p className="text-sm text-slate-400">
                    {filterQuery
                      ? 'No members match your search'
                      : 'No additional users have access to this project'}
                  </p>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      {/* Table Footer */}
      <div className="bg-white px-6 py-4 border-t border-slate-200 flex items-center justify-between">
        <span className="text-sm text-slate-500">
          {filterQuery
            ? `${filteredAcl.length} of ${aclLength} members`
            : `${totalMembers} member${memberPluralSuffix}`}
        </span>
      </div>
    </div>
  );
};

interface AddUserModalProps {
  onClose: () => void;
  searchQuery: string;
  onSearchChange: (value: string) => void;
  onSearchFocus: () => void;
  searchInputRef: React.RefObject<HTMLInputElement | null>;
  searchResultsRef: React.RefObject<HTMLDivElement | null>;
  showSearchResults: boolean;
  isSearching: boolean;
  searchResults: UserSearchResult[];
  selectedUser: UserSearchResult | null;
  onSelectUser: (user: UserSearchResult) => void;
  getInitials: (email: string) => string;
  newUserAccessLevel: 'read' | 'write';
  onAccessLevelChange: (level: 'read' | 'write') => void;
  onSubmit: () => void;
  isSubmitting: boolean;
  /** transfer: pick the new owner (no access level) */
  mode?: 'add' | 'transfer';
}

const AddUserModal: React.FC<AddUserModalProps> = ({
  onClose,
  searchQuery,
  onSearchChange,
  onSearchFocus,
  searchInputRef,
  searchResultsRef,
  showSearchResults,
  isSearching,
  searchResults,
  selectedUser,
  onSelectUser,
  getInitials,
  newUserAccessLevel,
  onAccessLevelChange,
  onSubmit,
  isSubmitting,
  mode = 'add'
}) => {
  const transfer = mode === 'transfer';
  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[1000] flex items-center justify-center p-4">
      <div className="relative w-full max-w-[500px] bg-white rounded-xl shadow-2xl overflow-hidden flex flex-col">
        {/* Modal Header */}
        <div className="flex items-center justify-between px-6 py-5 border-b border-slate-200">
          <h2 className="text-navy text-xl font-bold leading-tight tracking-tight">{transfer ? 'Transfer Ownership' : 'Add Member to Project'}</h2>
          <button
            onClick={onClose}
            className="text-slate-400 hover:text-navy transition-colors p-1 rounded-full hover:bg-slate-100"
          >
            <X size={22} />
          </button>
        </div>

        {/* Modal Body */}
        <div className="p-6 space-y-6">
          {/* Search Input */}
          <div>
            <label className="block text-sm font-medium text-navy mb-2">Find User</label>
            <div className="relative group">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Search size={18} className="text-slate-400 group-focus-within:text-primary transition-colors" />
              </div>
              <input
                ref={searchInputRef}
                type="text"
                value={searchQuery}
                onChange={(e) => onSearchChange(e.target.value)}
                onFocus={onSearchFocus}
                placeholder="Enter email or name..."
                className={`block w-full pl-10 pr-4 py-3 border rounded-lg text-navy placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary transition-all text-sm ${
                  selectedUser ? 'bg-emerald-50 border-emerald-300' : 'bg-surface-light border-slate-200'
                }`}
              />
            </div>
            <p className="text-xs text-slate-400 mt-1">Type at least 2 characters to search</p>

            {/* Search Results Dropdown */}
            <SearchDropdown
              ref={searchResultsRef}
              show={showSearchResults}
              query={searchQuery}
              isSearching={isSearching}
              searchResults={searchResults}
              selectedUserId={selectedUser?.userId}
              onSelect={onSelectUser}
              getInitials={getInitials}
            />
          </div>

          {transfer && (
            <p className="text-sm text-slate-600">
              The new owner gets full control of the project. The current owner keeps write access, and sessions they own in
              this project move to the new owner.
            </p>
          )}

          {/* Access Level */}
          {!transfer && <div className="space-y-3">
            <label className="block text-sm font-medium text-navy">Access Level</label>
            <label className={`flex items-start gap-3 p-4 rounded-lg border cursor-pointer transition-colors ${
              newUserAccessLevel === 'read'
                ? 'border-primary bg-primary/5'
                : 'border-slate-200 hover:border-primary/50 bg-white'
            }`}>
              <input
                type="radio"
                name="access_level"
                checked={newUserAccessLevel === 'read'}
                onChange={() => onAccessLevelChange('read')}
                className="mt-0.5 h-4 w-4 text-primary focus:ring-primary border-slate-300"
              />
              <div className="flex flex-col">
                <span className="text-navy text-sm font-medium">Read-only</span>
                <span className="text-slate-400 text-xs mt-0.5">Can view project data, sessions, and outputs.</span>
              </div>
            </label>
            <label className={`flex items-start gap-3 p-4 rounded-lg border cursor-pointer transition-colors ${
              newUserAccessLevel === 'write'
                ? 'border-primary bg-primary/5'
                : 'border-slate-200 hover:border-primary/50 bg-white'
            }`}>
              <input
                type="radio"
                name="access_level"
                checked={newUserAccessLevel === 'write'}
                onChange={() => onAccessLevelChange('write')}
                className="mt-0.5 h-4 w-4 text-primary focus:ring-primary border-slate-300"
              />
              <div className="flex flex-col">
                <span className="text-navy text-sm font-medium">Full Write Access</span>
                <span className="text-slate-400 text-xs mt-0.5">Can create/edit sessions, workflows, and upload files.</span>
              </div>
            </label>
          </div>}
        </div>

        {/* Modal Footer */}
        <div className="px-6 py-4 bg-slate-50 border-t border-slate-200 flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-lg text-sm font-medium text-slate-700 hover:bg-slate-200 border border-slate-200 bg-white transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={onSubmit}
            disabled={isSubmitting || !selectedUser}
            className={`px-4 py-2 rounded-lg text-sm font-medium text-white flex items-center gap-2 transition-all ${
              isSubmitting || !selectedUser
                ? 'bg-slate-300 cursor-not-allowed'
                : 'bg-primary hover:bg-primary/90 shadow-lg shadow-primary/20'
            }`}
          >
            {transfer ? <Crown size={16} /> : <UserPlus size={16} />}
            {transfer
              ? (isSubmitting ? 'Transferring...' : 'Transfer ownership')
              : (isSubmitting ? 'Adding...' : 'Add to Project')}
          </button>
        </div>
      </div>
    </div>
  );
};

interface DeleteConfirmModalProps {
  isOpen: boolean;
  userToDelete: ProjectACLEntry | null;
  onClose: () => void;
  onConfirm: () => void;
  isProcessing: boolean;
}

const DeleteConfirmModal: React.FC<DeleteConfirmModalProps> = ({
  isOpen,
  userToDelete,
  onClose,
  onConfirm,
  isProcessing
}) => (
  <ConfirmModal
    isOpen={isOpen}
    onClose={onClose}
    onConfirm={onConfirm}
    title="Revoke Access"
    message={`Are you sure you want to revoke access for ${userToDelete?.email}? They will no longer be able to access this project.`}
    confirmText="Revoke Access"
    cancelText="Cancel"
    variant="danger"
    isProcessing={isProcessing}
  />
);

export const AccessManagementTab: React.FC<AccessManagementTabProps> = ({
  projectId,
  projectOwnerId,
  canManageAcl,
  breakGlass
}) => {
  const { data: aclData, isLoading, error } = useProjectACL(projectId);
  const addACLMutation = useAddACLEntry(projectId, breakGlass);
  const removeACLMutation = useRemoveACLEntry(projectId, breakGlass);
  const updateACLMutation = useUpdateACLEntry(projectId, breakGlass);
  const transferMutation = useTransferOwnership(projectId, breakGlass);

  const acl = useMemo(() => aclData?.acl || [], [aclData?.acl]);
  const ownerEmail = aclData?.ownerEmail || '';
  const ownerId = aclData?.ownerId || projectOwnerId;

  const [showAddUserModal, setShowAddUserModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterQuery, setFilterQuery] = useState('');
  const [selectedUser, setSelectedUser] = useState<UserSearchResult | null>(null);
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [newUserAccessLevel, setNewUserAccessLevel] = useState<'read' | 'write'>('read');
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState<ProjectACLEntry | null>(null);
  const [userModalMode, setUserModalMode] = useState<'add' | 'transfer'>('add');
  const [transferTo, setTransferTo] = useState<string | null>(null);
  const [notification, setNotification] = useState<{
    type: 'success' | 'error';
    message: string;
  } | null>(null);

  const searchInputRef = useRef<HTMLInputElement>(null);
  const searchResultsRef = useRef<HTMLDivElement>(null);

  const { data: searchResults = [], isLoading: isSearching } = useSearchUsers(projectId, searchQuery);

  const isOwner = canManageAcl;

  // Stats
  const stats = useMemo(() => {
    const writeCount = acl.filter(e => e.accessLevel === 'write').length;
    const readCount = acl.filter(e => e.accessLevel === 'read').length;
    return {
      totalMembers: acl.length + 1, // +1 for owner
      writeCount,
      readCount
    };
  }, [acl]);

  // Filtered members
  const filteredAcl = useMemo(() => {
    if (!filterQuery) return acl;
    const q = filterQuery.toLowerCase();
    return acl.filter(e => e.email.toLowerCase().includes(q) || e.userId.toLowerCase().includes(q));
  }, [acl, filterQuery]);

  const showNotification = (type: 'success' | 'error', message: string) => {
    setNotification({ type, message });
    setTimeout(() => setNotification(null), 5000);
  };

  // Close search results when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      const outsideResults = !searchResultsRef.current?.contains(target);
      const outsideInput = !searchInputRef.current?.contains(target);
      if (outsideResults && outsideInput) setShowSearchResults(false);
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSearchChange = (value: string) => {
    setSearchQuery(value);
    setShowSearchResults(value.length >= 2);
    setSelectedUser(null);
  };

  const handleSelectUser = (user: UserSearchResult) => {
    setSelectedUser(user);
    setSearchQuery(user.email);
    setShowSearchResults(false);
  };

  const handleAddUser = async () => {
    if (!selectedUser) {
      showNotification('error', 'Please select a user from the search results');
      return;
    }
    if (userModalMode === 'transfer') {
      setTransferTo(selectedUser.email);
      return;
    }

    try {
      await addACLMutation.mutateAsync({
        email: selectedUser.email,
        accessLevel: newUserAccessLevel
      });

      showNotification('success', `Access granted to ${selectedUser.email}`);
      setShowAddUserModal(false);
      setSearchQuery('');
      setSelectedUser(null);
      setNewUserAccessLevel('read');
    } catch (error: any) {
      showNotification('error', `Failed to add user: ${error.response?.data?.error || error.message}`);
    }
  };

  const handleChangeLevel = async (entry: ProjectACLEntry, level: 'read' | 'write') => {
    if (level === entry.accessLevel) return;
    try {
      await updateACLMutation.mutateAsync({ userId: entry.userId, accessLevel: level });
      showNotification('success', `${entry.email} now has ${level} access`);
    } catch (error: any) {
      showNotification('error', `Failed to change access: ${error.response?.data?.error || error.message}`);
    }
  };

  const confirmTransfer = async () => {
    if (!transferTo) return;
    try {
      const { sessionsMoved } = await transferMutation.mutateAsync(transferTo);
      showNotification('success', `${transferTo} now owns this project${sessionsMoved ? `; ${sessionsMoved} session${sessionsMoved === 1 ? '' : 's'} moved` : ''}`);
      setTransferTo(null);
      handleModalClose();
    } catch (error: any) {
      showNotification('error', `Failed to transfer ownership: ${error.response?.data?.error || error.message}`);
      setTransferTo(null);
    }
  };

  const openUserModal = (mode: 'add' | 'transfer') => {
    setUserModalMode(mode);
    setShowAddUserModal(true);
  };

  const handleModalClose = () => {
    setShowAddUserModal(false);
    setSearchQuery('');
    setSelectedUser(null);
    setNewUserAccessLevel('read');
    setShowSearchResults(false);
  };

  const handleDeleteUser = (entry: ProjectACLEntry) => {
    setUserToDelete(entry);
    setDeleteConfirmOpen(true);
  };

  const confirmDeleteUser = async () => {
    if (!userToDelete) return;

    try {
      await removeACLMutation.mutateAsync(userToDelete.userId);
      showNotification('success', `Access revoked for ${userToDelete.email}`);
      setDeleteConfirmOpen(false);
      setUserToDelete(null);
    } catch (error: any) {
      showNotification('error', `Failed to remove user: ${error.response?.data?.error || error.message}`);
      setDeleteConfirmOpen(false);
    }
  };

  const getInitials = (email: string) => {
    const parts = email.split('@')[0].split(/[._-]/);
    if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
    return email.substring(0, 2).toUpperCase();
  };

  if (isLoading) {
    return (
      <div className="p-5 text-center text-slate-400">
        Loading access control list...
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-5 bg-amber-50 border border-amber-300 rounded-xl text-amber-700 flex items-center gap-2">
        <AlertCircle size={20} />
        Failed to load access control list
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-8">
      {/* Notification */}
      {notification && (
        <div className={`fixed top-20 right-5 px-4 py-3 rounded-xl text-sm font-medium shadow-lg z-[1000] ${
          notification.type === 'success'
            ? 'bg-emerald-50 border border-emerald-300 text-emerald-800'
            : 'bg-red-50 border border-red-300 text-red-800'
        }`}>
          {notification.message}
        </div>
      )}

      {/* Stats Row */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="flex flex-col gap-2 rounded-xl p-6 bg-white shadow-sm border border-slate-100">
          <div className="flex items-center justify-between">
            <p className="text-slate-500 text-sm font-medium uppercase tracking-wider">Total Members</p>
            <Users size={20} className="text-primary" />
          </div>
          <p className="text-navy text-3xl font-bold">{stats.totalMembers}</p>
        </div>
        <div className="flex flex-col gap-2 rounded-xl p-6 bg-white shadow-sm border border-slate-100">
          <div className="flex items-center justify-between">
            <p className="text-slate-500 text-sm font-medium uppercase tracking-wider">Access Levels</p>
            <Shield size={20} className="text-primary" />
          </div>
          <div className="flex gap-4 items-baseline">
            <p className="text-navy text-sm font-medium"><span className="font-bold text-lg">1</span> Owner</p>
            <p className="text-navy text-sm font-medium"><span className="font-bold text-lg">{stats.writeCount}</span> Write</p>
            <p className="text-navy text-sm font-medium"><span className="font-bold text-lg">{stats.readCount}</span> Read</p>
          </div>
        </div>
        <div className="flex flex-col gap-2 rounded-xl p-6 bg-white shadow-sm border border-slate-100">
          <div className="flex items-center justify-between">
            <p className="text-slate-500 text-sm font-medium uppercase tracking-wider">Project Owner</p>
            <Shield size={20} className="text-primary" />
          </div>
          <p className="text-navy text-xl font-bold truncate" title={ownerEmail}>{ownerEmail || '—'}</p>
        </div>
      </div>

      {/* Action Bar */}
      <div className="flex flex-col sm:flex-row justify-between gap-4 items-center bg-white p-4 rounded-xl border border-slate-100 shadow-sm">
        {isOwner && (
          <div className="flex w-full sm:w-auto gap-3">
            <button
              onClick={() => openUserModal('add')}
              className="w-full sm:w-auto flex cursor-pointer items-center justify-center rounded-lg h-10 bg-primary hover:bg-primary/90 transition-colors text-white gap-2 text-sm font-bold px-6 shadow-md shadow-primary/20"
            >
              <UserPlus size={18} />
              <span>Add Member</span>
            </button>
            <button
              onClick={() => openUserModal('transfer')}
              className="w-full sm:w-auto flex items-center justify-center gap-2 rounded-lg h-10 px-4 border border-slate-200 bg-white text-slate-600 text-sm font-bold hover:bg-slate-50 shadow-sm transition-colors"
            >
              <Crown size={18} />
              <span>Transfer Ownership</span>
            </button>
          </div>
        )}
        <div className="relative w-full sm:w-80 group">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <Search size={18} className="text-slate-400 group-focus-within:text-primary transition-colors" />
          </div>
          <input
            type="text"
            value={filterQuery}
            onChange={(e) => setFilterQuery(e.target.value)}
            className="block w-full pl-10 pr-3 py-2.5 border-none rounded-lg bg-surface-light text-navy placeholder-slate-400 focus:ring-2 focus:ring-primary/50 text-sm"
            placeholder="Search members by name or email..."
          />
        </div>
      </div>

      {/* Members Table */}
      <MembersTable
        ownerEmail={ownerEmail}
        ownerId={ownerId}
        isOwner={isOwner}
        filteredAcl={filteredAcl}
        aclLength={acl.length}
        totalMembers={stats.totalMembers}
        filterQuery={filterQuery}
        onDeleteUser={handleDeleteUser}
        onChangeLevel={handleChangeLevel}
        onMakeOwner={(entry) => setTransferTo(entry.email)}
        busy={updateACLMutation.isPending || transferMutation.isPending}
        getInitials={getInitials}
      />

      {/* Add User Modal */}
      {showAddUserModal && (
        <AddUserModal
          onClose={handleModalClose}
          searchQuery={searchQuery}
          onSearchChange={handleSearchChange}
          onSearchFocus={() => searchQuery.length >= 2 && setShowSearchResults(true)}
          searchInputRef={searchInputRef}
          searchResultsRef={searchResultsRef}
          showSearchResults={showSearchResults}
          isSearching={isSearching}
          searchResults={searchResults}
          selectedUser={selectedUser}
          onSelectUser={handleSelectUser}
          getInitials={getInitials}
          newUserAccessLevel={newUserAccessLevel}
          onAccessLevelChange={setNewUserAccessLevel}
          onSubmit={handleAddUser}
          isSubmitting={addACLMutation.isPending || transferMutation.isPending}
          mode={userModalMode}
        />
      )}

      {/* Transfer Ownership Confirmation */}
      <ConfirmModal
        isOpen={!!transferTo}
        onClose={() => setTransferTo(null)}
        onConfirm={confirmTransfer}
        title="Transfer Ownership"
        message={`Make ${transferTo} the owner of this project? ${ownerEmail || 'The current owner'} keeps write access, and sessions they own in this project move to ${transferTo}.${breakGlass ? ' This is recorded as a break-glass change.' : ''}`}
        confirmText="Transfer ownership"
        cancelText="Cancel"
        variant="warning"
        isProcessing={transferMutation.isPending}
      />

      {/* Delete Confirmation Modal */}
      <DeleteConfirmModal
        isOpen={deleteConfirmOpen}
        userToDelete={userToDelete}
        onClose={() => {
          setDeleteConfirmOpen(false);
          setUserToDelete(null);
        }}
        onConfirm={confirmDeleteUser}
        isProcessing={removeACLMutation.isPending}
      />
    </div>
  );
};
